import { useEffect, useState, useCallback } from "react";
import { Droplet, Utensils, Syringe, Scale } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";

const PURP_LABEL = { basal: "Basal", meal_correction: "Refeição / correção", unspecified: "Não informada" };
const METHOD_LABEL = { per_100g: "Por 100 g", direct: "Direto", photo: "Foto", mixed: "Misto" };
const dayOf = (dt) => { const s = new Date(dt).toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); };

const r2 = (n) => Math.round(n * 100) / 100;

const TABS = [
  { id: "glucose", key: "nav_glucose" },
  { id: "meals", key: "nav_meals" },
  { id: "doses", key: "nav_doses" },
];

export default function History() {
  const { t } = useI18n();
  const { activeProfileId } = useProfile();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("glucose");

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const [g, m, d] = await Promise.all([
      api.get(`/profiles/${activeProfileId}/glucose`),
      api.get(`/profiles/${activeProfileId}/meals`),
      api.get(`/profiles/${activeProfileId}/doses`),
    ]);
    const all = [
      ...g.data.map((r) => ({ type: "glucose", dt: r.datetime, title: `${r.value} ${r.unit}`, sub: r.source === "sensor" ? t("src_sensor") : t("src_capillary"), author: r.author_name, id: r.id, mg: r.unit === "mmol/L" ? r.value * 18 : r.value, unit: r.unit })),
      ...m.data.map((r) => ({ type: "meals", dt: r.datetime, title: `${r.notes || t("reg_meal")} · ${r.total_carbs} g`, sub: METHOD_LABEL[r.method] || r.method, author: r.author_name, id: r.id, carbs: Number(r.total_carbs) || 0 })),
      ...d.data.map((r) => ({ type: "doses", dt: r.datetime, title: `${r.units} U ${r.insulin_name || ""}`, sub: PURP_LABEL[r.purpose] || r.purpose, author: r.author_name, id: r.id, units: Number(r.units) || 0, purpose: r.purpose })),
    ].sort((a, b) => new Date(b.dt) - new Date(a.dt));
    setItems(all);
  }, [activeProfileId, t]);

  useEffect(() => { load(); }, [load]);

  const ICON = { glucose: Droplet, meals: Utensils, doses: Syringe, weight: Scale };
  const filtered = items.filter((i) => i.type === tab);
  const groups = [];
  {
    filtered.forEach((i) => {
      const day = dayOf(i.dt);
      let g = groups[groups.length - 1];
      if (!g || g.day !== day) { g = { day, items: [], total: 0, basal: 0, meal: 0, other: 0, mgSum: 0, carbs: 0, units: new Set() }; groups.push(g); }
      g.items.push(i);
      if (tab === "glucose") { g.mgSum += i.mg; g.units.add(i.unit); return; }
      if (tab === "meals") { g.carbs += i.carbs; return; }
      g.total += i.units;
      if (i.purpose === "basal") g.basal += i.units;
      else if (i.purpose === "meal_correction") g.meal += i.units;
      else g.other += i.units;
    });
  }

  const summary = {};
  items.forEach((i) => {
    const d = dayOf(i.dt);
    const s = summary[d] || (summary[d] = { mg: 0, n: 0, carbs: 0, ins: 0, units: new Set() });
    if (i.type === "glucose") { s.mg += i.mg; s.n += 1; s.units.add(i.unit); }
    else if (i.type === "meals") s.carbs += i.carbs;
    else if (i.type === "doses") s.ins += i.units;
  });

  const DaySummary = ({ day, gi }) => {
    const s = summary[day];
    const cell = (Icon, label, value, tid) => (
      <div className="flex items-center gap-1.5 min-w-0" data-testid={`${tid}-${gi}`}>
        <Icon size={13} className="text-[#1A73E8] shrink-0 hidden sm:block" />
        <span className="min-w-0 leading-tight">
          <span className="block text-[10px] uppercase tracking-wide text-[#627D98]">{label}</span>
          <span className="block text-[13px] sm:text-sm font-semibold text-[#0D253F] whitespace-nowrap">{value}</span>
        </span>
      </div>
    );
    return (
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2 rounded-xl border border-[#E3EFF9] bg-[#F7FAFD] px-3 py-2 my-2" data-testid={`day-summary-${gi}`}>
        {cell(Droplet, "Glicemia", s.n ? glucoseAvg({ mgSum: s.mg, items: { length: s.n }, units: s.units }) : "—", "day-sum-glucose")}
        {cell(Utensils, "Hidratos", `${r2(s.carbs)} g`, "day-sum-carbs")}
        {cell(Syringe, "Insulina", `${r2(s.ins)} U`, "day-sum-insulin")}
      </div>
    );
  };

  const glucoseAvg = (g) => {
    const mg = g.mgSum / g.items.length;
    return g.units.size === 1 && g.units.has("mmol/L") ? `${(mg / 18).toFixed(1)} mmol/L` : `${Math.round(mg)} mg/dL`;
  };

  const renderItem = (i) => {
    const Icon = ICON[i.type] || Droplet;
    const fmt = { hour: "2-digit", minute: "2-digit" };
    return (
      <div key={i.id} className="flex items-center gap-3 py-3" data-testid={`history-item-${i.id}`}>
        <span className="h-9 w-9 rounded-lg bg-[#E3EFF9] flex items-center justify-center shrink-0"><Icon size={16} className="text-[#1A73E8]" /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#0D253F] truncate">{i.title}</div>
          <div className="text-xs text-[#627D98] truncate">{i.sub} · {i.author}</div>
        </div>
        <span className="text-xs text-[#627D98] shrink-0">{new Date(i.dt).toLocaleString("pt-PT", fmt)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("history_title")}</h1>
        <p className="text-[#627D98] mt-1">{t("history_sub")}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`history-tab-${tb.id}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${tab === tb.id ? "bg-[#1A73E8] text-white" : "bg-white border border-[#E3EFF9] text-[#3B526B]"}`}>
            {t(tb.key)}
          </button>
        ))}
      </div>

      <div className="df-card p-5">
        {filtered.length === 0 ? (
          <p className="text-sm text-[#627D98]">Sem registos.</p>
        ) : tab === "doses" ? (
          <div className="space-y-5">
            {groups.map((g, gi) => (
              <div key={g.day} data-testid={`dose-day-${gi}`}>
                <div className="text-xs font-semibold tracking-wide text-[#627D98] pb-1 border-b border-[#E3EFF9]">{g.day}</div>
                <DaySummary day={g.day} gi={gi} />
                <div className="divide-y divide-[#F0F5FA]">{g.items.map(renderItem)}</div>
                <div className="mt-1 rounded-xl bg-[#E3EFF9] px-4 py-2.5 space-y-1">
                  <div className="flex items-center justify-between text-sm text-[#3B526B]" data-testid={`dose-day-basal-${gi}`}>
                    <span>Basal</span><span className="font-semibold">{r2(g.basal)} U</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-[#3B526B]" data-testid={`dose-day-meal-${gi}`}>
                    <span>Refeição / correção</span><span className="font-semibold">{r2(g.meal)} U</span>
                  </div>
                  {g.other > 0 && (
                    <div className="flex items-center justify-between text-sm text-[#3B526B]" data-testid={`dose-day-other-${gi}`}>
                      <span>Não informada</span><span className="font-semibold">{r2(g.other)} U</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-[#B8D4F8] pt-1.5" data-testid={`dose-day-total-${gi}`}>
                    <span className="text-sm font-semibold text-[#0D3C78]">Total de Insulina do Dia</span>
                    <span className="font-head text-base font-bold text-[#0D253F]">{r2(g.total)} U</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "glucose" ? (
          <div className="space-y-5">
            {groups.map((g, gi) => (
              <div key={g.day} data-testid={`glucose-day-${gi}`}>
                <div className="text-xs font-semibold tracking-wide text-[#627D98] pb-1 border-b border-[#E3EFF9]">{g.day}</div>
                <DaySummary day={g.day} gi={gi} />
                <div className="divide-y divide-[#F0F5FA]">{g.items.map(renderItem)}</div>
                <div className="mt-1 flex items-center justify-between rounded-xl bg-[#E3EFF9] px-4 py-2.5" data-testid={`glucose-day-avg-${gi}`}>
                  <span className="text-sm font-semibold text-[#0D3C78]">Média do Dia <span className="font-normal text-[#627D98]">· {g.items.length} {g.items.length === 1 ? "leitura" : "leituras"}</span></span>
                  <span className="font-head text-base font-bold text-[#0D253F]">{glucoseAvg(g)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((g, gi) => (
              <div key={g.day} data-testid={`meal-day-${gi}`}>
                <div className="text-xs font-semibold tracking-wide text-[#627D98] pb-1 border-b border-[#E3EFF9]">{g.day}</div>
                <DaySummary day={g.day} gi={gi} />
                <div className="divide-y divide-[#F0F5FA]">{g.items.map(renderItem)}</div>
                <div className="mt-1 flex items-center justify-between rounded-xl bg-[#E3EFF9] px-4 py-2.5" data-testid={`meal-day-total-${gi}`}>
                  <span className="text-sm font-semibold text-[#0D3C78]">Total de Hidratos do Dia <span className="font-normal text-[#627D98]">· {g.items.length} {g.items.length === 1 ? "refeição" : "refeições"}</span></span>
                  <span className="font-head text-base font-bold text-[#0D253F] shrink-0">{r2(g.carbs)} g</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
